import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaFileInvoice, FaPlus, FaReceipt } from "react-icons/fa";
import axiosInstance from "../../../api/axiosInstance";
import { getStatusInfo } from "../../../utils/session";
import { showGlobalToast } from "../../../utils/toast";

interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  instructions?: string;
  status: string;
}

interface Order {
  orderId: string;
  orderTime: string;
  items: OrderItem[];
}

type CustomerDetails = {
  id?: string;
  customerName?: string;
  customerEmail?: string;
  customerMobile?: string;
  customerCount?: number;
  waiterName?: string;
};

interface TableOrderDetailsProps {
  tableId: string;
  tableLabel: string;
  onBack: () => void;
  onBill: () => void;
  onNewOrder: (details: CustomerDetails) => void;
}

const statusTone: Record<string, string> = {
  placed: "bg-orange-50 ring-1 ring-orange-200 dark:bg-orange-500/10 dark:ring-orange-500/20",
  accepted: "bg-purple-50 ring-1 ring-purple-200 dark:bg-purple-500/10 dark:ring-purple-500/20",
  preparing: "bg-amber-50 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/20",
  ready: "bg-emerald-50 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:ring-emerald-500/20",
  delivered: "bg-emerald-50 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:ring-emerald-500/20",
  completed: "bg-emerald-50 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:ring-emerald-500/20",
  cancelled: "bg-red-50 ring-1 ring-red-200 dark:bg-red-500/10 dark:ring-red-500/20",
  unknown: "bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700",
};

const TableOrderDetails: React.FC<TableOrderDetailsProps> = ({
  tableId,
  tableLabel,
  onBack,
  onBill,
  onNewOrder,
}) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails | null>(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/Billing/GetBillDetails/table/${tableId}`);
        const billData = response.data;

        if (billData && billData.orders) {
          const transformedOrders: Order[] = billData.orders.map((order: any) => ({
            orderId: order.orderId,
            orderTime: order.orderTime,
            items: order.items.map((item: any) => ({
              id: item.id || `${order.orderId}-${item.name}`,
              name: item.name,
              quantity: item.qty || item.quantity,
              instructions: item.instructions || "",
              status: item.status || "processing",
            })),
          }));

          setOrders(transformedOrders);
          setCustomerDetails({
            id: billData.customerId || "",
            customerName: billData.customerName || "Guest",
            customerEmail: billData.customerEmail || "",
            customerMobile: billData.customerMobile || "",
            customerCount: billData.customerCount || 1,
            waiterName: billData.waiterName || "",
          });
        } else {
          setOrders([]);
          setCustomerDetails(null);
        }
      } catch (error: any) {
        console.error("Error fetching order details:", error);
        showGlobalToast(error.response?.data?.message || "Error fetching order details", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [tableId]);

  const totalItems = orders.reduce(
    (sum, order) => sum + order.items.reduce((itemSum, item) => itemSum + item.quantity, 0),
    0
  );

  const getNormalizedStatus = (status: string) => {
    const normalized = status?.trim().toLowerCase();

    if (!normalized || normalized === "processing") {
      return "Preparing";
    }

    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const renderOrderStatus = (status: string) => {
    const normalizedStatus = getNormalizedStatus(status);
    const statusInfo = getStatusInfo(normalizedStatus);
    const StatusIcon = statusInfo.icon;
    const tone = statusTone[normalizedStatus.toLowerCase()] || statusTone.unknown;

    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${tone} ${statusInfo.color}`}
      >
        <StatusIcon size={12} />
        {statusInfo.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="flex flex-col items-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-orange-500" />
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">Loading order details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 text-slate-900 dark:bg-gray-900 dark:text-slate-100">
      <header className="sticky top-0 z-20 border-b border-gray-200/80 bg-gray-100/90 backdrop-blur-xl dark:border-slate-800 dark:bg-gray-900/90">
        <div className="mx-auto max-w-5xl px-3 py-2.5 sm:px-4 sm:py-3">
          <div className="flex items-start gap-3">
            <button
              onClick={onBack}
              className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700"
            >
              <FaArrowLeft size={14} />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-base font-black text-slate-950 dark:text-slate-100 sm:text-xl">
                {tableLabel}
              </h1>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span className="max-w-full truncate rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
                  {customerDetails?.customerName || "Guest"}
                </span>
                <span className="rounded-full bg-slate-950 px-2.5 py-1 text-[11px] font-semibold text-white dark:bg-slate-100 dark:text-slate-900">
                  {totalItems} item{totalItems === 1 ? "" : "s"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-3 py-4 pb-32 sm:px-4">
        {orders.length === 0 ? (
          <div className="rounded-[1.8rem] border border-dashed border-orange-200 bg-white/85 px-6 py-14 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900/70">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-orange-50 text-orange-500 dark:bg-orange-500/10 dark:text-orange-300">
              <FaReceipt size={20} />
            </div>
            <p className="mt-4 text-base font-semibold text-slate-800 dark:text-slate-200">
              No orders found for this table.
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Start a new order to begin service from this screen.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr),280px] xl:items-start">
            <div className="space-y-3">
              {orders.map((order) => (
                <section
                  key={order.orderId}
                  className="overflow-hidden rounded-[1.65rem] border border-white/70 bg-white/95 shadow-[0_14px_34px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-slate-900/95"
                >
                  <div className="border-b border-orange-100 bg-[linear-gradient(180deg,#fffaf5_0%,#ffffff_100%)] px-4 py-3 dark:border-slate-800 dark:bg-[linear-gradient(180deg,rgba(30,41,59,0.95)_0%,rgba(15,23,42,0.95)_100%)]">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                          Order #{order.orderId}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {new Date(order.orderTime).toLocaleString()}
                        </p>
                      </div>
                      <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {order.items.reduce((sum, item) => sum + item.quantity, 0)} item
                        {order.items.reduce((sum, item) => sum + item.quantity, 0) === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 p-3">
                    {order.items.map((item) => (
                      <div key={item.id} className="rounded-[1.2rem] bg-slate-50 px-3 py-3 dark:bg-slate-800/70">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {item.name}
                            </p>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              Qty {item.quantity}
                            </p>
                            {item.instructions && (
                              <p className="mt-2 rounded-xl bg-white px-2.5 py-2 text-xs italic text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
                                Note: {item.instructions}
                              </p>
                            )}
                          </div>
                          {renderOrderStatus(item.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <aside className="hidden xl:block xl:sticky xl:top-[7.5rem]">
              <div className="rounded-[1.65rem] border border-white/70 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/90">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                  Live session
                </p>
                <p className="mt-2 text-base font-black text-slate-900 dark:text-slate-100">
                  {customerDetails?.customerName || "Guest"}
                </p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{tableLabel}</p>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <div className="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-800/70">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      Orders
                    </p>
                    <p className="mt-1 text-lg font-black text-slate-900 dark:text-slate-100">
                      {orders.length}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-orange-50 px-3 py-3 dark:bg-orange-500/10">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-orange-700 dark:text-orange-300">
                      Items
                    </p>
                    <p className="mt-1 text-lg font-black text-orange-800 dark:text-orange-200">
                      {totalItems}
                    </p>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        )}
      </main>

      <div className="fixed inset-x-0 bottom-0 z-30 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-4 md:px-6 lg:left-64 lg:right-0 lg:px-8">
        <div className="mx-auto w-full max-w-5xl">
          <div className="grid grid-cols-2 gap-3 rounded-[1.8rem] border border-white/60 bg-slate-950/92 p-3 shadow-[0_24px_60px_rgba(15,23,42,0.4)] backdrop-blur-xl dark:border-slate-700/80">
            <button
              onClick={onBill}
              className="inline-flex items-center justify-center gap-2 rounded-[1.2rem] bg-white px-4 py-3 text-sm font-semibold text-slate-900 dark:bg-slate-800 dark:text-slate-100"
            >
              <FaFileInvoice size={14} />
              Bill
            </button>
            <button
              onClick={() => onNewOrder(customerDetails || { customerName: "Guest" })}
              className="inline-flex items-center justify-center gap-2 rounded-[1.2rem] bg-orange-500 px-4 py-3 text-sm font-semibold text-white"
            >
              <FaPlus size={13} />
              New Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableOrderDetails;
